import type { AppConfigInfo } from "../api/types";
import { useT } from "../i18n";

export interface BusinessPolicyStatusProps {
  config: AppConfigInfo | null;
  context: "shell" | "rules" | "settings";
  compact?: boolean;
  /** Settings can place the same provenance in its shared System Status disclosure. */
  showTechnicalDetails?: boolean;
}

export function BusinessPolicyStatus({
  config,
  context,
  compact = false,
  showTechnicalDetails = true,
}: BusinessPolicyStatusProps) {
  const t = useT();
  const runtime = config?.rule_parameters;
  const available =
    config?.rules_authority === "code_fixed" &&
    runtime?.status === "healthy" &&
    runtime.feature_enabled === false &&
    (runtime.source === "code_defaults" || runtime.source === "tenant_set");
  // A business with owner-set values (#1617) no longer runs the shared defaults.
  const tenantValues = available && runtime.source === "tenant_set";

  return (
    <section
      className={`business-policy-status business-policy-status--${context}${compact ? " business-policy-status--compact" : ""}`}
      data-status={available ? "available" : "unavailable"}
      role={available ? "status" : "alert"}
      aria-live="polite"
    >
      <div className="business-policy-status__primary">
        <span
          className={`business-policy-status__dot ${available ? "dot-ok" : "dot-warn"}`}
          aria-hidden="true"
        />
        <div className="business-policy-status__copy">
          <strong>
            {available ? t("policy.fixedCode") : t("policy.unavailable")}
          </strong>
          {!compact && (
            <span>
              {available
                ? t(tenantValues ? "policy.tenantValuesHelp" : "policy.availableHelp")
                : t("policy.unavailableHelp")}
            </span>
          )}
        </div>
      </div>

      {!compact && showTechnicalDetails && !available && (
        <p className="business-policy-status__error">
          {t("policy.diagnosticCode")}:{" "}
          <code>{runtime?.error_code ?? t("common.notSet")}</code>
        </p>
      )}

      {!compact && showTechnicalDetails && available && runtime && (
        <details className="business-policy-status__details">
          <summary>{t("policy.technicalDetails")}</summary>
          <dl>
            <TechnicalItem label={t("policy.codeRuleset")} value={config.ruleset_version} />
            <TechnicalItem
              label={t("policy.runtimeSource")}
              value={`${config.rules_authority} / ${runtime.source}`}
            />
            <TechnicalItem
              label={t("policy.effectiveHash")}
              value={runtime.effective_rules_hash ?? t("common.notSet")}
              mono
            />
          </dl>
        </details>
      )}
    </section>
  );
}

function TechnicalItem({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string | number;
  mono?: boolean;
}) {
  return (
    <div>
      <dt>{label}</dt>
      <dd className={mono ? "neo-mono" : undefined}>{value}</dd>
    </div>
  );
}
